import { useParams, Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { IMG_BASE, MARQUEE_BRANDS } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

export default function FaviconPage() {
  const { domain } = useParams<{ domain: string }>();

  if (!domain) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Missing domain</h1>
          <Button asChild><Link to="/"><ArrowLeft className="w-4 h-4" />Back to Home</Link></Button>
        </div>
      </div>
    );
  }

  const displayName = domain.replace(/\.(com|io|org|co|tv|us|so|dev|net)$/, "");

  return (
    <PageLayout breadcrumbs={[{ label: "Favicons", to: "/" }, { label: domain }]}>
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0 flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-white border shadow-sm">
            <img
              src={`${IMG_BASE}/favicon/${domain}`}
              alt={`${domain} favicon`}
              className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
              onError={(e) => { (e.target as HTMLElement).style.opacity = "0.3"; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 capitalize">{displayName} Favicon</h1>
            <p className="text-base text-secondary-foreground leading-relaxed mb-3">
              Favicon for <span className="font-mono text-sm">{domain}</span>
            </p>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">Favicon</Badge>
              <Badge variant="outline">{domain}</Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/favicon/${domain}" />`} />
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">How It Works</h2>
        <div className="bg-card border rounded-xl p-5 space-y-3 text-sm text-secondary-foreground">
          <p>LoadLogo tries multiple favicon sources in order: apple-touch-icon, high-res favicons, then falls back to Google's favicon service.</p>
          <p>For full brand logos with colors and metadata, use the <Link to={`/logo/${domain}`} className="text-primary hover:underline">brand logo endpoint</Link> instead.</p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Favicon image -->\n<img src="${IMG_BASE}/favicon/${domain}" />\n\n<!-- Full brand logo (higher quality) -->\n<img src="${IMG_BASE}/${domain}" />\n\n<!-- Compare: Favicon vs Brand Logo -->\n<img src="${IMG_BASE}/favicon/${domain}" /> <!-- smaller, icon-sized -->\n<img src="${IMG_BASE}/${domain}" />          <!-- full brand logo -->`} />
      </section>

      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Popular Favicons</h2>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          {MARQUEE_BRANDS.filter((b) => b !== domain).slice(0, 16).map((d) => (
            <Link key={d} to={`/favicon/${d}`} className="flex flex-col items-center gap-2 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all no-underline">
              <img src={`${IMG_BASE}/favicon/${d}`} alt={d} className="w-8 h-8 object-contain" loading="lazy" />
              <span className="text-[10px] text-muted-foreground truncate w-full text-center">{d.replace(/\.(com|io|org|co|tv|us|so)$/, "")}</span>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
